import { Link, useLocation } from 'react-router';
import { Dica } from '@/componentes/Dica';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { type ItemMenu, itemAtivo, menuAgrupado } from './itens-menu';

// `min-h-11` = 44px: alvo de toque mínimo (docs/CONVENCOES-UI.md, §Acessibilidade).
const CLASSE_ITEM =
  'flex min-h-11 items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors';

/**
 * Item que a pessoa pode ver mas ainda não pode abrir. `aria-disabled` em vez de sumir com ele: quem
 * tem a permissão enxerga que a área existe, e o motivo fica no `Tooltip` **e** em `sr-only` — um
 * tooltip não chega a quem navega por teclado com leitor de tela.
 */
function ItemDesabilitado({ item }: { item: ItemMenu }) {
  const Icone = item.icone;
  return (
    <Dica texto={item.indisponivel} lado="right">
      <span
        aria-disabled="true"
        tabIndex={0}
        className={cn(CLASSE_ITEM, 'cursor-not-allowed text-muted-foreground opacity-60')}
      >
        <Icone className="size-5 shrink-0" aria-hidden />
        {item.rotulo}
        <span className="sr-only">— indisponível: {item.indisponivel}</span>
      </span>
    </Dica>
  );
}

function ItemLink({ item, aoNavegar }: { item: ItemMenu; aoNavegar?: () => void }) {
  // O item de prefixo mais longo acende sozinho: `/leads/listas` é "Listas de leads", não também "Leads".
  const ativo = itemAtivo(useLocation().pathname) === item.para;
  const Icone = item.icone;
  return (
    <Dica texto={item.dica} lado="right">
      <Link
        to={item.para}
        aria-current={ativo ? 'page' : undefined}
        onClick={aoNavegar}
        className={cn(
          CLASSE_ITEM,
          ativo
            ? // Dourado como fundo translúcido + texto `foreground`: o estado ativo é marca, e o
              // rótulo continua em marfim/grafite, que é o par legível (docs/MARCA.md §3).
              'bg-primary/15 text-foreground'
            : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
        )}
      >
        <Icone className={cn('size-5 shrink-0', ativo && 'text-primary')} aria-hidden />
        {item.rotulo}
      </Link>
    </Dica>
  );
}

/**
 * Lista de navegação, usada tanto na barra lateral (≥ md) quanto na gaveta do mobile. Itens
 * agrupados ganham um cabeçalho de seção — a lista continua sendo uma só, com `aria-label` no
 * `nav`, sem menu aninhado.
 */
export function Navegacao({ aoNavegar }: { aoNavegar?: () => void }) {
  const { tem } = useAuth();

  return (
    <nav aria-label="Navegação principal" className="flex flex-col gap-1">
      {menuAgrupado(tem).map((secao) => (
        <div
          key={secao.grupo ?? 'principal'}
          className={secao.grupo ? 'mt-4 space-y-1' : 'space-y-1'}
        >
          {secao.grupo ? (
            <p className="px-3 pb-1 text-xs font-medium tracking-wide text-muted-foreground uppercase">
              {secao.grupo}
            </p>
          ) : null}
          {secao.itens.map((item) =>
            item.indisponivel ? (
              <ItemDesabilitado key={item.rotulo} item={item} />
            ) : (
              <ItemLink key={item.rotulo} item={item} aoNavegar={aoNavegar} />
            ),
          )}
        </div>
      ))}
    </nav>
  );
}
